import type { ComponentsPage, TGetComponentsFx } from './index.h'

import { api } from '@setplex/tria-api'
import { attach, createEffect, createStore } from 'effector'
import { features, options } from '~/core/flags'
import { appearanceFeature } from '~/features/appearance'
import { model as guest } from '~/features/subscriber/guest'
import { modules } from '~/shared/config'
import { logger } from '~/shared/logger'
import { filterRowsVisibility } from './lib'

/*
 * Effect to load components by module identifier.
 */
const getFilteredComponentsFx = createEffect(
  async ({ source, params }: TGetComponentsFx): Promise<ComponentsPage> => {
    let page: Omit<ComponentsPage, 'structureOrigin'>

    try {
      page = await api.appearance.getModuleComponentsFx(params)
    } catch (error) {
      logger.error(`Failed to load ${params.identifier} components:`, error)
      throw error
    }

    const components: ComponentsPage = {
      ...page,
      structureOrigin: 'backend',
    }

    components.content = filterRowsVisibility(components, source)

    return components
  }
)

/*
 * Store to put effect that "loads components".
 */
export const $customFx = createStore<Record<
  string,
  typeof getFilteredComponentsFx
> | null>(null)

export const getComponentsFx = attach({
  source: {
    features: features.$featuredFlags,
    options: options.$optionsFlags,
    modules: modules.$visibility,
    appearanceModules: appearanceFeature.$modules,
    identifire: appearanceFeature.$currentIdentifier,
    isGuest: guest.$guest,
    customFx: $customFx,
  },
  async effect(source, params) {
    try {
      const customFx = source.customFx?.[params.identifier]

      // Home page, f.e. has custom "load components" effect.
      if (customFx) return customFx({ source, params })

      return getFilteredComponentsFx({ source, params })
    } catch (error) {
      logger.error(
        `Failed to choose effect for ${params.identifier} rows:`,
        error
      )
      throw error
    }
  },
})

export const $isRowsLoading = getComponentsFx.pending
